import { yupResolver } from '@hookform/resolvers/yup'
import { useEstimateMonthlyPayment, useGetPromotionalCreditAppPlan } from '@src/api/compute-api'
import { FilteredCreditApplication, useSubmitHardHit } from '@src/api/credit-api'
import {
  AcceptTermsCheckbox,
  AsyncActionButton,
  ExpandableSection,
  Loader,
  ManageCoappButton,
  SinInput,
} from '@src/components'
import RemoveCoappButton from '@src/components/RemoveCoappButton'

import {
  formatCurrencyNoDecimals,
  formatCurrencyRoundedUpToNoDecimals,
  formatCurrencyToNearestUpperMultipleToNoDecimals,
} from '@if/core/utils'
import { Merchant } from '@src/api/merchants-api'
import {
  EditSinAndLoanAmount,
  buildEditSinAndLoanAmountSchema,
} from '@src/containers/ViewCreditApplication/components/EditPrequalificationSchema'
import {
  getPrequalifiedAmount,
  getPrequalifiedMaxTerm,
  mustAskApplicantSIN,
  mustAskCoapplicantSIN,
} from '@src/data/credit-application-selectors'
import { reportErrorToConsole } from '@src/services/error-logger'
import { useTrackStepReachedEvent } from '@src/services/trackingEvents'
import { Constants, ESessionStorageKeys } from '@src/types'
import { ETrackedEvents } from '@src/types/tracking'
import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import ApprovedSoftHitAndHardHitFooter from '../../components/ApprovedSoftHitAndHardHitFooter'

interface Props {
  creditApp: FilteredCreditApplication
  merchant: Merchant
}

const ApprovedPrequalForm = ({ creditApp, merchant }: Props) => {
  const { t } = useTranslation()

  const initialPrequalifiedAmount = getPrequalifiedAmount(creditApp)
  const initialPrequalifiedTerm = getPrequalifiedMaxTerm(creditApp)
  const [promotionalPlan] = useGetPromotionalCreditAppPlan(
    initialPrequalifiedAmount,
    creditApp.id,
    creditApp.merchantPaymentPlanId,
  )

  const maxLoanTerm: number = creditApp.merchantPaymentPlanId
    ? (promotionalPlan?.loanTerm ?? 0)
    : (initialPrequalifiedTerm ?? 0)

  const prequalificationParams = useMemo(
    () => ({
      term: initialPrequalifiedTerm,
      amount: initialPrequalifiedAmount,
      minInterestRate: creditApp.prequalificationDecision.minInterestRate,
      maxInterestRate: creditApp.prequalificationDecision.maxInterestRate,
      adminFeeRate: merchant.merchantBaseRates?.lenderFeeRate ?? Constants.FinanceFeeRateForRegular * 100,
    }),
    [creditApp, initialPrequalifiedAmount, initialPrequalifiedTerm, merchant],
  )

  const [estimateResult, isFetchingEstimate] = useEstimateMonthlyPayment(prequalificationParams)
  const minPaymentForInitialApprovedAmount = estimateResult?.minPaymentAmount
  const maxPaymentForInitialApprovedAmount = estimateResult?.maxPaymentAmount

  const askApplicantSIN = mustAskApplicantSIN(creditApp)
  const askCoapplicantSIN = mustAskCoapplicantSIN(creditApp)
  const { requestedLoanAmount } = creditApp
  const maxLoanAmout = creditApp.prequalificationDecision.maxLoanAmount

  const shouldProposeCoapplicant = maxLoanAmout < requestedLoanAmount && creditApp.applicant.hasCreditReport
  const hasCoapplicant = creditApp.coapplicant != null

  useTrackStepReachedEvent(creditApp, ETrackedEvents.PrequalSuccess)

  const {
    handleSubmit,
    formState: { errors },
    register,
  } = useForm<EditSinAndLoanAmount>({
    mode: 'onBlur',
    defaultValues: {
      needsApplicantSin: askApplicantSIN,
      needsCoapplicantSin: askCoapplicantSIN === true,
      requestedLoanAmount: initialPrequalifiedAmount,
      id: creditApp.id,
    },
    resolver: yupResolver(buildEditSinAndLoanAmountSchema(creditApp.prequalificationDecision.maxLoanAmount)),
  })

  const [submitHardHit, isSubmitting] = useSubmitHardHit()
  const innerSubmit = useCallback(
    (data: EditSinAndLoanAmount) => {
      submitHardHit(data).catch(reportErrorToConsole)
      sessionStorage.removeItem(ESessionStorageKeys.PREQUAL_INFO)
    },
    [submitHardHit],
  )

  if (isFetchingEstimate) {
    return <Loader title={t('common.withYouShortly')} description={t('common.pleaseWait')} />
  }

  return (
    <main className="general-message" style={{ display: 'block' }}>
      <form onSubmit={handleSubmit(innerSubmit)}>
        <h1 className="h3" style={{ lineHeight: 1.5 }}>
          {creditApp.merchantPaymentPlan === null && (
            <Trans
              i18nKey="prequalification.prequalified"
              values={{
                requestedAmount: formatCurrencyRoundedUpToNoDecimals(initialPrequalifiedAmount),
                minMonthlyPayment: formatCurrencyNoDecimals(minPaymentForInitialApprovedAmount ?? 0),
                maxMonthlyPayment: formatCurrencyNoDecimals(maxPaymentForInitialApprovedAmount ?? 0),
              }}
            />
          )}

          {creditApp.merchantPaymentPlan !== null && promotionalPlan?.paymentForFrequency && (
            <Trans
              i18nKey="prequalification.prequalifiedVariableInterest"
              values={{
                requestedAmount: formatCurrencyRoundedUpToNoDecimals(initialPrequalifiedAmount),
                monthlyPayment: formatCurrencyToNearestUpperMultipleToNoDecimals(
                  promotionalPlan.paymentForFrequency,
                  5,
                ),
              }}
            />
          )}
        </h1>
        <div className="paragraph">
          <div className="information">
            <Trans i18nKey="prequalification.loanDetails.mainDetails" />
          </div>
          <ExpandableSection toggleText="prequalification.loanDetails.learnMore">
            <p
              style={{
                fontStyle: 'italic',
                border: '1px solid #08abf0',
                borderRadius: '1.5rem',
                textAlign: 'center',
                padding: '1.5rem',
              }}
            >
              {t('prequalification.loanDetails.learnMoreDetails')}
            </p>
          </ExpandableSection>

          {shouldProposeCoapplicant && (
            <div style={{ marginTop: '32px', marginBottom: '32px' }}>
              <div className="strong">{t('prequalification.addCoapplicant.title')}</div>
              <div className="btn-group" style={{ marginTop: '16px' }}>
                {hasCoapplicant && <RemoveCoappButton creditAppId={creditApp.id} />}
                <ManageCoappButton creditAppId={creditApp.id} useChangeLabel={hasCoapplicant} />
              </div>
            </div>
          )}

          {askApplicantSIN && (
            <SinInput
              error={errors.applicantSin}
              id="applicantSin"
              reason="prequalification.applicantCreditLessThan2Years"
              {...register('applicantSin')}
            />
          )}

          {askCoapplicantSIN && (
            <SinInput
              error={errors.coapplicantSin}
              id="coapplicantSin"
              reason="prequalification.coapplicantCreditLessThan2Years"
              {...register('coapplicantSin')}
            />
          )}

          <div style={{ marginTop: '32px' }}>{t('prequalification.pullCreditReport')}</div>
          <AcceptTermsCheckbox
            id="acceptConditions"
            termId={`${String(t(hasCoapplicant ? 'prequalification.acceptConditionsWithCoapplicant' : 'prequalification.acceptConditions'))}²`}
            msgOnInvalid={t('common.acceptConditions')}
          />
        </div>
        <div className="btn-group mobile-reversed justify-end">
          <AsyncActionButton type="submit" isPending={isSubmitting} primary style={{ justifyContent: 'center' }}>
            <span>
              {shouldProposeCoapplicant
                ? t('common.continueWith', { amount: formatCurrencyRoundedUpToNoDecimals(initialPrequalifiedAmount) })
                : t('prequalification.continue')}
            </span>
            <i className="fa-regular fa-arrow-right" />
          </AsyncActionButton>
        </div>
        <ApprovedSoftHitAndHardHitFooter
          merchantPaymentPlan={creditApp.merchantPaymentPlan}
          minInterestRate={creditApp.prequalificationDecision.minInterestRate}
          hasCoapplicant={hasCoapplicant}
          maxLoanTerm={maxLoanTerm}
        />
      </form>
    </main>
  )
}

export default ApprovedPrequalForm
